import { logger } from "@/util/logger";
import type { ReactNode } from "react";
import React from "react";
import { addErrorLog } from "@/services/axios/unProtectedApi";
import {
  isChunkLoadError,
  recoverFromChunkLoadError,
} from "@/util/chunkLoadRecovery";

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  /** True while a chunk-load reload is in flight — avoid flashing Oops. */
  recoveringChunkLoad: boolean;
};

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      recoveringChunkLoad: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    if (isChunkLoadError(error)) {
      return {
        hasError: false,
        error: null,
        errorInfo: null,
        recoveringChunkLoad: true,
      };
    }

    return {
      hasError: true,
      error,
      errorInfo: null,
      recoveringChunkLoad: false,
    };
  }

  async componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (isChunkLoadError(error)) {
      if (!recoverFromChunkLoadError()) {
        this.setState({
          hasError: true,
          recoveringChunkLoad: false,
          error,
          errorInfo,
        });
      }
      return;
    }

    this.setState({ errorInfo });

    if (process.env.NEXT_PUBLIC_ENV !== "prod") return;

    try {
      await addErrorLog({
        userId: localStorage.getItem("userId") as any,
        error: {
          name: error.name,
          message: error.message,
        },
        errorDesc: errorInfo,
      });
    } catch (err) {
      logger.log("err", err);
    }
  }

  render() {
    if (this.state.recoveringChunkLoad) {
      return null;
    }

    if (this.state.hasError) {
      return (
        <section className="relative z-10 bg-primary py-[120px] h-screen flex items-center">
          <div className="container mx-auto">
            <div className="flex">
              <div className="w-full px-4">
                <div className="mx-auto max-w-[600px] text-center">
                  <h2 className="mb-2 text-[50px] font-bold text-white sm:text-[50px] md:text-[80px]">
                    Oops!
                  </h2>
                  <h4 className="my-3 text-[22px] font-semibold leading-tight text-white">
                    Something went wrong, please try again.
                  </h4>
                  <button
                    type="button"
                    className="inline-block rounded-lg border border-white mt-8 px-8 py-3 text-center text-base font-semibold text-white transition hover:bg-cardBg hover:text-primary"
                    onClick={() => window.location.reload()}
                  >
                    Retry
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
